import { AutoComplete } from "frontend/src/components/AutoComplete/AutoComplete";
import { useQuery } from "@apollo/client";
import * as types from "_graphql-types/graphql";

import i18n from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  removeFilter,
  updateFilter,
} from "Reducers/globalSearchV2/globalSearchV2.actions";
import { getCompanyFilters } from "Reducers/globalSearchV2/globalSearchV2.selectors";
import { FilterInterface } from "../FilterInterface";
import { ADDRESS_SEARCH } from "../graphql";
import { useDispatch } from "src/react-redux";

function onlyUnique(location: any, index: number, self: any[]) {
  return (
    index ===
    self.findIndex(
      t =>
        t.country === location.country &&
        t.countryEnumId === location.countryEnumId &&
        t.state === location.state &&
        t.stateEnumId === location.stateEnumId
    )
  );
}

const locationText = (location: any) =>
  [location.city, location.state, location.country]
    .filter((v: string | null) => v)
    .join(", ");

const options = (locations?: types.AddressSearchQuery["addressSearch"]) => {
  if (!locations) {
    return [];
  }
  const countryOnly = locations
    .map(location => ({
      country: location.country,
      countryEnumId: location.countryEnumId,
    }))
    .filter(onlyUnique);
  const countryState = locations
    .map(location => ({
      state: location.state,
      stateEnumId: location.stateEnumId,
    }))
    .filter(location => location.state)
    .filter(onlyUnique);

  return [...locations, ...countryOnly, ...countryState];
};

export const CompanyHeadquartersFilter = () => {
  const companyFilters = useSelector(getCompanyFilters);

  const dispatch = useDispatch();

  const headquartersFilter = useMemo(() => {
    for (const filter of companyFilters) {
      if (filter.type === "HEADQUARTERS") return filter.data;
    }
  }, [companyFilters]);

  const defaultFilter: types.LocationFilter = {};

  const [localFilter, setLocalFilter] = useState(
    headquartersFilter ?? defaultFilter
  );

  const locationSelect = (v: any, option: any) => {
    const record = option.location;
    setLocalFilter({
      countryEnum: record.countryEnumId,
      stateEnum: record.stateEnumId,
      city: record.city,
    });
  };

  const [query, changeQuery] = useState("");

  const { data } = useQuery(ADDRESS_SEARCH, {
    variables: { q: query, isCompany: true },
    skip: query.length < 4,
  });

  const set = useCallback(() => {
    if (localFilter.countryEnum || localFilter.stateEnum || localFilter.city)
      dispatch(
        updateFilter({
          type: "company",
          data: {
            type: "HEADQUARTERS",
            data: localFilter,
          },
        })
      );
  }, [localFilter]);

  const reset = useCallback(() => {
    changeQuery("");
    dispatch(
      removeFilter({
        type: "company",
        data: {
          type: "HEADQUARTERS",
          data: defaultFilter,
        },
      })
    );
  }, []);

  return (
    <FilterInterface
      label={i18n.t("search_discovery.filters.labels.location")}
      count={headquartersFilter ? 1 : 0}
      reset={reset}
      set={set}
      menuClass="filters__menu-dropdown"
    >
      <div className="main-dropdown__menu-body">
        <AutoComplete
          data-cy="Headquarters-Auto-Complete"
          onSelect={locationSelect}
          onChange={(v: any) => {
            changeQuery(v);
          }}
          options={options(data?.addressSearch).map(
            (location: types.AddressSearchItem) => ({
              label: locationText(location),
              value: locationText(location),
              location,
            })
          )}
        />
      </div>
    </FilterInterface>
  );
};
